import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getLogs, type LogRecord, type GetLogsParams } from "../api/client";
import type { ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseLogsOptions {
  serviceSlug: ServiceSlug | null;
  timeRange?: TimeRange;
  serviceNames?: string[];
  serviceInstances?: string[];
  traceIds?: string[];
  spanIds?: string[];
  logIds?: string[];
  severityRanges?: string[];
  bodyFilters?: { text: string; mode: "contains" | "prefix" | "suffix" | "equals" }[];
  sort?: string;
  order?: string;
  refreshToken?: number;
  enabled?: boolean;
  limit?: number;
}

export interface UseLogsResult {
  logs: LogRecord[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

/**
 * Hook for fetching logs with filters.
 *
 * @param options - Configuration options for fetching logs
 * @returns Object containing logs, loading state, and error
 */
export function useLogs({
  serviceSlug,
  timeRange,
  serviceNames = [],
  serviceInstances = [],
  traceIds = [],
  spanIds = [],
  logIds = [],
  severityRanges = [],
  bodyFilters = [],
  sort,
  order,
  refreshToken = 0,
  enabled = true,
  limit,
}: UseLogsOptions): UseLogsResult {
  const serviceNamesKey = serviceNames.join(",");
  const serviceInstancesKey = serviceInstances.join(",");
  const traceIdsKey = traceIds.join(",");
  const spanIdsKey = spanIds.join(",");
  const logIdsKey = logIds.join(",");
  const severityKey = severityRanges.join(",");
  const bodyFiltersKey = JSON.stringify(bodyFilters);
  const timeFrom = timeRange?.from ? timeRange.from.toISOString() : null;
  const timeTo = timeRange?.to ? timeRange.to.toISOString() : null;

  const hasAnyQuery =
    serviceNames.length > 0 ||
    serviceInstances.length > 0 ||
    traceIds.length > 0 ||
    spanIds.length > 0 ||
    logIds.length > 0 ||
    !!timeRange;

  const query = useQuery({
    queryKey: [
      "logs",
      serviceSlug,
      timeFrom,
      timeTo,
      serviceNamesKey,
      serviceInstancesKey,
      traceIdsKey,
      spanIdsKey,
      logIdsKey,
      severityKey,
      bodyFiltersKey,
      sort ?? null,
      order ?? null,
      limit ?? null,
      refreshToken,
    ],
    placeholderData: keepPreviousData,
    enabled: !!serviceSlug && enabled && hasAnyQuery,
    queryFn: async ({ signal }) => {
      const params: GetLogsParams = {
        serviceSlug: serviceSlug!,
        startTime: timeRange?.from,
        endTime: timeRange?.to,
        serviceNames,
        serviceInstances,
        traceIds,
        spanIds,
        logIds,
        severityRanges,
        bodyFilters,
        sort,
        order,
        limit,
        signal,
      };
      const results = await getLogs(params);
      return limit ? results.slice(0, limit) : results;
    },
  });

  if (!serviceSlug || !enabled || !hasAnyQuery) {
    return { logs: [], loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error ? query.error.message : query.error ? "Failed to load logs" : null;
  return {
    logs: query.data ?? [],
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
